import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  getAdminGetLegalDocumentsQueryKey,
  useAdminCreateLegalDocumentVersion,
} from '@/api/auth/admin/admin';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/services/logger';

interface AddLegalDocumentVersionDialogProps {
  documentType: 'privacy-policy' | 'terms-of-use';
  documentTitle: string;
}

/**
 * Dialog for adding a new legal document version
 */
export function AddLegalDocumentVersionDialog({
  documentType,
  documentTitle,
}: AddLegalDocumentVersionDialogProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [version, setVersion] = useState('');
  const [englishUrl, setEnglishUrl] = useState('');

  const { mutate: createVersion, isPending: isCreating } = useAdminCreateLegalDocumentVersion({
    mutation: {
      onSuccess: (data) => {
        toast.success('Version created successfully');
        setIsOpen(false);
        resetForm();
        queryClient.invalidateQueries({
          queryKey: getAdminGetLegalDocumentsQueryKey(),
        });
        // Navigate to the new version page
        if (data.data?.version) {
          navigate(`/dashboard/admin/legal-documents/${documentType}/versions/${data.data.version}`);
        }
      },
      onError: (error: any) => {
        logger.error(error, {
          context: 'CreateLegalDocumentVersion',
          documentType,
          version: version.trim(),
        });
        toast.error('Failed to create version. ' + (error.response?.data?.detail || error.message));
      },
    },
  });

  const resetForm = () => {
    setVersion('');
    setEnglishUrl('');
  };

  const handleCreate = () => {
    // Validate URL
    try {
      new URL(englishUrl.trim());
    } catch {
      toast.error('Please enter a valid URL');
      return;
    }

    createVersion({
      type: documentType,
      data: {
        version: version.trim(),
        links: [
          {
            language: 'en',
            url: englishUrl.trim(),
          },
        ],
      },
    });
  };

  const handleClose = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      resetForm();
    }
  };

  const isValid = version.trim() && englishUrl.trim();

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="h-4 w-4 mr-2" />
          New Version
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New Version</DialogTitle>
          <DialogDescription>
            Create a new version of the {documentTitle}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="version">Version</Label>
            <Input
              id="version"
              type="text"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="1.0"
              maxLength={50}
            />
            <p className="text-sm text-muted-foreground">
              Version identifier (e.g., "1.0", "2024-01", "2.0.1")
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="englishUrl">
              English URL <span className="text-destructive">*</span>
            </Label>
            <Input
              id="englishUrl"
              type="url"
              value={englishUrl}
              onChange={(e) => setEnglishUrl(e.target.value)}
              placeholder="https://example.com/legal/privacy-policy/en"
            />
            <p className="text-sm text-muted-foreground">
              URL to the English version of the document. Additional languages can be added after
              creation.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={isCreating || !isValid}>
            {isCreating ? 'Creating...' : 'Create Version'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
